/**
 * Spatial hash over enemies (the small set): 64px power-of-two cells,
 * flat-array bucket chains, rebuilt from scratch each tick (≤64 inserts —
 * incremental bookkeeping isn't worth its bugs). Zero allocation after
 * construction; query dedupe via visit stamps.
 */

const CELL_SHIFT = 6; // 64px
const GRID_BITS = 6; // 64x64 cells wraps a 4096px world
const BUCKETS = 1 << (GRID_BITS * 2);

export interface HashItem {
  x: number;
  y: number;
  radius: number;
}

export class SpatialHash {
  private heads = new Int32Array(BUCKETS).fill(-1);
  private touchedBuckets: Int32Array;
  private touchedCount = 0;
  private entryItem: Int32Array;
  private entryNext: Int32Array;
  private entryCount = 0;
  private stamps: Int32Array;
  private queryId = 0;

  constructor(maxItems = 256, maxEntries = 1024) {
    this.entryItem = new Int32Array(maxEntries);
    this.entryNext = new Int32Array(maxEntries);
    this.stamps = new Int32Array(maxItems).fill(-1);
    this.touchedBuckets = new Int32Array(maxEntries);
  }

  rebuild(items: readonly HashItem[]): void {
    // Clear only buckets we dirtied last tick.
    for (let i = 0; i < this.touchedCount; i++) {
      this.heads[this.touchedBuckets[i]!] = -1;
    }
    this.touchedCount = 0;
    this.entryCount = 0;

    for (let i = 0; i < items.length; i++) {
      const it = items[i]!;
      const minCx = (it.x - it.radius) >> CELL_SHIFT;
      const maxCx = (it.x + it.radius) >> CELL_SHIFT;
      const minCy = (it.y - it.radius) >> CELL_SHIFT;
      const maxCy = (it.y + it.radius) >> CELL_SHIFT;
      for (let cy = minCy; cy <= maxCy; cy++) {
        for (let cx = minCx; cx <= maxCx; cx++) {
          if (this.entryCount >= this.entryItem.length) return; // saturated: degrade silently
          const bucket = (cx & ((1 << GRID_BITS) - 1)) | ((cy & ((1 << GRID_BITS) - 1)) << GRID_BITS);
          const e = this.entryCount++;
          this.entryItem[e] = i;
          this.entryNext[e] = this.heads[bucket]!;
          if (this.heads[bucket] === -1) this.touchedBuckets[this.touchedCount++] = bucket;
          this.heads[bucket] = e;
        }
      }
    }
  }

  /** Visit each item whose cells overlap the query circle (dedup guaranteed). */
  query(x: number, y: number, r: number, visit: (itemIndex: number) => void): void {
    const id = ++this.queryId;
    const minCx = (x - r) >> CELL_SHIFT;
    const maxCx = (x + r) >> CELL_SHIFT;
    const minCy = (y - r) >> CELL_SHIFT;
    const maxCy = (y + r) >> CELL_SHIFT;
    for (let cy = minCy; cy <= maxCy; cy++) {
      for (let cx = minCx; cx <= maxCx; cx++) {
        const bucket = (cx & ((1 << GRID_BITS) - 1)) | ((cy & ((1 << GRID_BITS) - 1)) << GRID_BITS);
        for (let e = this.heads[bucket]!; e !== -1; e = this.entryNext[e]!) {
          const item = this.entryItem[e]!;
          if (this.stamps[item] === id) continue;
          this.stamps[item] = id;
          visit(item);
        }
      }
    }
  }
}
